/**
 * WizardPreviewModal — Bedenimi Bul wizard'ının ana görünümü.
 *
 * State context'ten gelir (`useWizardCtx`). Modalın kendisi sadece render +
 * step orkestrasyonu + backend recommendation effect'leri + host DOM
 * auto-select / stock check'leri. State ve adım geçişleri Provider'da.
 */
import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { ArrowLeft, X, Pencil } from "lucide-react";
import { analyzeProfile } from "@/lib/find-my-size-profile";
import { MeasuringTapeLoader } from "@/components/find-my-size/MeasuringTapeLoader";
import { getSayzSessionId, sendSayzEvent } from "@/lib/sayz-widget-session";
import { RESULT_LOADER_MIN_MS, stubRecommend, withPreviewDefaults } from "./constants";
import { useWizardCtx } from "./context";
import { performHostAutoSelect, checkHostSizeStock } from "./host-integration";
import { ProgressBar } from "./parts/ProgressBar";
import { FooterAction } from "./parts/FooterAction";
import { WelcomeStep } from "./steps/WelcomeStep";
import { BasicInfoStep } from "./steps/BasicInfoStep";
import { FitPrefStep } from "./steps/FitPrefStep";
import { BodyStep } from "./steps/BodyStep";
import { ResultStep } from "./steps/ResultStep";

export const WizardPreviewModal = () => {
    const {
        accent, settings, modalTitle, profile, setField, step, previewRefusal,
        goNext, goBack, goToStepId, requiredBodyFields, visibleSteps,
        lastAnsweredField, widgetTemplate, widgetTokens, sizeTableButtonText,
        onClose, onOpenSizeChart, liveSiteKey, liveSku, liveApiBase,
    } = useWizardCtx();

    const currentId = visibleSteps[step];
    // Template DNA flags:
    //   Editorial → modern lüks sans, hairline, radius 0
    //   Bold      → Field Notes, chunky 700, 2px border, radius 0
    const isEditorial = widgetTemplate === "editorial";
    const isBold = widgetTemplate === "bold";
    // Backend live mode mi: siteKey + sku varsa backend algoritmasını kullan.
    // Yoksa dashboard preview (stub) mantığı.
    const liveMode = !!(liveSiteKey && liveSku && liveApiBase);

    const canProceed = useMemo(() => {
        if (currentId === "welcome") return true;
        if (currentId === "basic") return !!(profile.height && profile.weight && profile.age);
        if (currentId === "fit") return !!profile.fit;
        if (currentId?.startsWith("body:")) return !!profile[currentId.split(":")[1]];
        return true;
    }, [currentId, profile]);

    const autoAdvance = (field, value) => {
        setField(field, value);
        setTimeout(() => goNext(), 380);
    };

    const stepCopy = settings.stepCopy || {};
    const resultCopy = settings.resultCopy || {};
    const smartRecall = settings.smartRecall || { enabled: true, fastPathMode: "auto_recommend" };

    const previewProfile = useMemo(() => withPreviewDefaults(profile), [profile]);
    const welcomeAnalysis = useMemo(
        () => analyzeProfile(profile, requiredBodyFields),
        [profile, requiredBodyFields],
    );

    // ─── Smooth height transition ─────────────────────────────────────────
    // Modal body yüksekliği içeriğe göre dinamik; adım değişiminde ve içerik
    // yeniden boyutlanmasında `ResizeObserver` ile ölçülüp explicit px'e set
    // edilir. Böylece `transition: height 350ms` çalışır (CSS doğal `auto`
    // → `px` geçişini desteklemediği için manuel ölçüm şart). `overflow:
    // hidden` sayesinde scroll yok, modal kendisi yumuşakça büyür / küçülür.
    const contentRef = useRef(null);
    const [measuredHeight, setMeasuredHeight] = useState(null);

    // ─── Backend recommendation state (live mode) ───────────────────────
    // Live context'te backend endpoint'e POST atılır, result cache'lenir.
    // Preview context'inde (`liveMode === false`) `stubRecommend` fallback
    // devrededir — merchant panelde algoritma çağırmaya gerek yok.
    const [liveRecommendation, setLiveRecommendation] = useState(null);
    const [liveWelcomeRec, setLiveWelcomeRec] = useState(null);

    useEffect(() => {
        if (!liveMode) return;
        // Welcome ekranı için: profile complete ise ve fastPathMode uygunsa
        // backend'den fetch et ve "Karşılama · Tam" ekranındaki beden kutusunu
        // doldur. "Partial" / "empty" welcome mode'ları için zaten gereksiz.
        if (!welcomeAnalysis.complete) {
            setLiveWelcomeRec(null);
            return;
        }
        let cancelled = false;
        const startedAt = Date.now();
        setLiveWelcomeRec(null);

        // Same "measuring tape ritual" the result step uses — the welcome
        // recommend screen is functionally identical (big 72 px size letter
        // + chips) so it deserves the exact same minimum ritual window.
        // Without this, cached visitors would see the size pop in with no
        // sense of "we computed something" — same error-flash problem.
        const settleWithRitual = (payload) => {
            if (cancelled) return;
            const elapsed = Date.now() - startedAt;
            const wait = Math.max(0, RESULT_LOADER_MIN_MS - elapsed);
            if (wait === 0) {
                setLiveWelcomeRec(payload);
            } else {
                setTimeout(() => {
                    if (!cancelled) setLiveWelcomeRec(payload);
                }, wait);
            }
        };

        (async () => {
            try {
                const r = await fetch(`${liveApiBase}/api/widget/suggest-size`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        key: liveSiteKey,
                        sku: liveSku,
                        profile,
                        session_id: getSayzSessionId(),
                    }),
                });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const data = await r.json();
                if (cancelled) return;
                if (data && data.found) {
                    // Beacon the suggestion so the Analitik → Dönüşüm funnel
                    // can compute "modal_open → size_suggested" conversion
                    // per session. Welcome fast-path logs here too — the
                    // visitor hasn't clicked yet but the system HAS produced
                    // a recommendation, so it counts.
                    sendSayzEvent(liveApiBase, "size_suggested", {
                        widget: "find-my-size",
                        key: liveSiteKey,
                        sku: liveSku,
                        size: data.size,
                    });
                    settleWithRitual({ size: data.size, outOfRange: data.outOfRange, confidence: data.confidence });
                } else {
                    settleWithRitual(null);
                }
            } catch (_) {
                settleWithRitual(null);
            }
        })();
        return () => { cancelled = true; };
    }, [liveMode, liveApiBase, liveSiteKey, liveSku, profile, welcomeAnalysis.complete]);

    useEffect(() => {
        if (!liveMode) return;
        // Result ekranı için canlı backend çağrısı
        if (currentId !== "result") {
            // Result'tan çıkıldığında recommendation state'ini sıfırlama;
            // kullanıcı welcome'a döndüğünde cache korunur.
            return;
        }
        let cancelled = false;
        const startedAt = Date.now();
        setLiveRecommendation(null); // loader state

        // Resolve the loader into the real recommendation, honouring the
        // minimum ritual window. If the backend was faster than
        // RESULT_LOADER_MIN_MS we hold; if it was slower, we flip right
        // away. This gives every visitor the same "deliberate" micro-
        // moment regardless of their network / backend latency.
        const settleWithRitual = (payload) => {
            if (cancelled) return;
            const elapsed = Date.now() - startedAt;
            const wait = Math.max(0, RESULT_LOADER_MIN_MS - elapsed);
            if (wait === 0) {
                setLiveRecommendation(payload);
            } else {
                setTimeout(() => {
                    if (!cancelled) setLiveRecommendation(payload);
                }, wait);
            }
        };

        (async () => {
            try {
                const r = await fetch(`${liveApiBase}/api/widget/suggest-size`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        key: liveSiteKey,
                        sku: liveSku,
                        profile,
                        session_id: getSayzSessionId(),
                    }),
                });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const data = await r.json();
                if (cancelled) return;
                if (data && data.found) {
                    sendSayzEvent(liveApiBase, "size_suggested", {
                        widget: "find-my-size",
                        key: liveSiteKey,
                        sku: liveSku,
                        size: data.size,
                    });
                    settleWithRitual({ size: data.size, outOfRange: data.outOfRange, confidence: data.confidence });
                } else {
                    settleWithRitual({ size: null, outOfRange: true, confidence: 0 });
                }
            } catch (_) {
                settleWithRitual({ size: null, outOfRange: true, confidence: 0 });
            }
        })();
        return () => { cancelled = true; };
    }, [liveMode, liveApiBase, liveSiteKey, liveSku, profile, currentId]);

    // Fast-path recommendation ("Karşılama · Tam" senaryosunda büyük beden gösterimi için)
    // Live mode'da backend sonucunu, preview mode'da stubRecommend döndür.
    const welcomeRecommendation = useMemo(() => {
        if (liveMode) return liveWelcomeRec;
        return stubRecommend(previewProfile);
    }, [liveMode, liveWelcomeRec, previewProfile]);

    const recommendation = useMemo(() => {
        if (currentId !== "result") return null;
        if (liveMode) return liveRecommendation;
        return stubRecommend(previewProfile);
    }, [liveMode, liveRecommendation, currentId, previewProfile]);

    // Host DOM stock status for whichever size we're about to show. We
    // intentionally re-compute on every render (dependencies: size + the
    // merchant's siteIntegration config), so a merchant SPA that live-
    // mutates its stock classes between "add to cart" transitions reflects
    // instantly. The probe is cheap: one querySelectorAll + a filter. We
    // ONLY run it in live mode so the admin preview stays clean — merchants
    // reviewing their config in the panel shouldn't see "stokta yok" cues
    // just because they're testing from a URL without the storefront DOM.
    const activeSizeForStock = (recommendation?.size) || (welcomeRecommendation?.size) || null;
    const hostStockStatus = useMemo(() => {
        if (!liveMode) return "disabled";
        if (!activeSizeForStock) return "disabled";
        return checkHostSizeStock(activeSizeForStock, settings.siteIntegration).status;
    }, [liveMode, activeSizeForStock, settings.siteIntegration]);

    // Welcome varyantı — FooterAction ve WelcomeStep aynı karara göre render eder.
    const welcomeMode = useMemo(() => {
        if (!smartRecall.enabled) return "empty";
        if (!welcomeAnalysis.hasAnyData) return "empty";
        if (!welcomeAnalysis.complete) return "partial";
        return smartRecall.fastPathMode === "always_confirm" ? "confirm" : "recommend";
    }, [smartRecall, welcomeAnalysis]);

    // Step transition key — welcomeMode/partial-field değişimlerinde de animasyon
    const stepKey = `${currentId}${previewRefusal ? "-refusal" : ""}${currentId === "welcome" ? `-${welcomeMode}-${welcomeAnalysis.missing.join(",")}` : ""}`;

    // ─── Content height observer ─────────────────────────────────────────
    // Her stepKey değişiminde yeni DOM subtree render edilir; ref yeni içeriği
    // gösterir, effect yeniden kurulur, `ResizeObserver` içerik yüksekliğini
    // dinler. `scrollHeight` kullanıyoruz çünkü parent'ın explicit height'ı
    // set edildikten sonra `offsetHeight` parent constraint'ine kilitlenir —
    // `scrollHeight` içeriğin gerçek doğal yüksekliğini verir, animasyon
    // delay'li reveal'ler (`sayz-fms-reveal`) sonrası doğru tetiklenir.
    useEffect(() => {
        const el = contentRef.current;
        if (!el) return;
        const update = () => {
            const h = el.scrollHeight;
            if (h > 0) setMeasuredHeight(h);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [stepKey]);

    const goToBasic = useCallback(() => goToStepId("basic"), [goToStepId]);
    const goToResult = useCallback(() => goToStepId("result"), [goToStepId]);

    // Welcome · Partial "Devam Et" — welcome partial moddayken, eksik alan
    // bir BODY field ise WelcomeStep inline mini-soru render eder ve seçince
    // auto-advance ile ilerler. Ama eksik alan BASIC field ise (fit / height
    // / weight / age), basic'in UI'ı daha büyük (number input / 3 radio
    // kart) olduğu için inline mini-soru göstermiyoruz — bunun yerine footer
    // CTA'sı ile ilgili tam step'e götürüyoruz. Değilse "Bir şey daha
    // soracağım" ekranı deadlock olurdu (fit missing → ne inline soru ne CTA
    // ne auto-advance → user takılı kalır). Field → step id eşlemesi:
    //   "fit"                       → fit step
    //   "height" / "weight" / "age" → basic step (üçü aynı ekranda)
    const onPartialContinue = useCallback(() => {
        const firstBasic = welcomeAnalysis.missingBasic[0];
        if (firstBasic === "fit") goToStepId("fit");
        else goToStepId("basic");
    }, [welcomeAnalysis.missingBasic, goToStepId]);

    // "Bedeni Seç" CTA handler. In live mode (merchant's site via Shadow DOM),
    // this attempts to auto-select the recommended size on the merchant's own
    // product page via `performHostAutoSelect`. In preview mode (admin panel),
    // there are no host size buttons, so the DOM scan simply fails fast with
    // `code: "disabled"` or `code: "no-elements"` — in both cases we just
    // close the modal (same as the pre-integration behaviour).
    //
    // UX decision for MVP: always close the modal after the attempt so the
    // visitor sees the merchant page react (success) or can hand-pick the
    // size (failure). We surface the attempt result on `window` so the
    // merchant can debug via browser dev tools without us having to render
    // an error banner inside the shadow DOM.
    const handleSelectSize = useCallback(() => {
        // The recommended size source depends on where the click originated.
        // On the welcome fast-path (`recommend` mode) the recommendation is
        // computed as `welcomeRecommendation`; on the result step it lives
        // in `recommendation`. Fall back between them so this one callback
        // works for both CTAs without the FooterAction having to care.
        const size = recommendation?.size || welcomeRecommendation?.size;
        // Beacon the acceptance BEFORE auto-selecting on the host DOM —
        // sendBeacon is unload-safe so even if the merchant's size button
        // click immediately triggers navigation, the funnel count survives.
        // Semantic contract (documented for the Analitik tooltip):
        //   `size_accepted` = ziyaretçi önerilen bedeni almayı seçti ve
        //   "Bedeni Seç" CTA'sına tıkladı (host DOM'da auto-select buradan
        //   tetiklenir). Satın alma veya sepete ekleme sinyali DEĞİL.
        if (liveMode && size) {
            sendSayzEvent(liveApiBase, "size_accepted", {
                widget: "find-my-size",
                key: liveSiteKey,
                sku: liveSku,
                size,
            });
        }
        try {
            const result = performHostAutoSelect(size, settings?.siteIntegration);
            if (typeof window !== "undefined") {
                // Expose last attempt for merchant debugging. Non-standard but
                // intentional — easier than wiring a logging endpoint for an
                // MVP integration feature.
                window.__sayzLastAutoSelect = result;
                if (!result.ok && result.code !== "disabled") {
                    // Surface the failure in the console so a merchant
                    // investigating "why didn't my size button click?" has
                    // immediate signal. Success is silent.
                    // eslint-disable-next-line no-console
                    console.warn("[Sayz] Auto-select failed:", result);
                }
            }
        } catch (_) {
            /* swallow — the host page shouldn't crash because of us */
        }
        if (typeof onClose === "function") onClose();
    }, [recommendation, welcomeRecommendation, settings, onClose, liveMode, liveApiBase, liveSiteKey, liveSku]);

    // "Bilgilerimi Değiştir" link'i sadece bilinen bir profil varken görünür —
    // wizard'ı basic'ten başlatır. Empty welcome ve mid-wizard (basic/fit/body)
    // step'lerinde anlamsız. Nazik Ret'te de gösteriyoruz — kullanıcı isterse
    // ölçüsünü değiştirebilir.
    const showEditLink = useMemo(() => {
        if (currentId === "welcome") {
            return welcomeMode === "partial" || welcomeMode === "confirm" || welcomeMode === "recommend";
        }
        if (currentId === "result") return true; // Refusal dâhil
        return false;
    }, [currentId, welcomeMode]);

    // Geri butonu sadece mid-wizard step'lerinde anlamlı (fit + body). Welcome
    // ilk adım (isFirst), basic zaten başlangıç noktası, result terminal.
    const showBackButton = useMemo(() => {
        return !["welcome", "basic", "result"].includes(currentId);
    }, [currentId]);

    return (
        <div
            className={`${(isEditorial || isBold) ? "" : "rounded-xl "}bg-surface border ${isEditorial ? "border-foreground/20" : isBold ? "border-border/80" : "border-border/80"} flex flex-col sayz-anim-fade overflow-hidden`}
            data-testid="fms-preview-modal"
            data-editorial={isEditorial ? "true" : "false"}
            data-bold={isBold ? "true" : "false"}
            style={{
                // Modal shell artık içeriğin doğal boyutuna uyar — alt
                // envelope `h-auto` + `transition: height 350ms` ile her
                // step'te yumuşakça büyüyüp küçülür. Shell üzerinde
                // `max-height` yok çünkü her constraint Welcome Partial'ı
                // buduyordu (587 px desktop / 684 px mobile). Host sayfa
                // kendi `max-height` constraint'ini (örn. `100dvh - 24px`)
                // dışardan uygular; standalone preview'da modal content
                // kadardır. Mobile'da bu "sheet-like takeover" hissi
                // verir (Linear/Stripe mobile pattern).
                "--sayz-scroll-accent": accent,
                // Font weight baseline:
                //   Editorial → 300 light (lüks)
                //   Bold      → 450 medium-light (field notes "lite" — nefes alan)
                fontWeight: isEditorial ? 300 : isBold ? 450 : undefined,
                borderRadius: (isEditorial || isBold) ? 0 : undefined,
                borderWidth: isEditorial ? 0.5 : isBold ? 0.5 : undefined,
            }}
        >
            {/* ====================================================================
                EDITORIAL SCOPED CSS — İtalyan lüks: radius 0 + light tipografi
                ==================================================================== */}
            {isEditorial && (
                <style>{`
                    [data-testid="fms-preview-modal"][data-editorial="true"] button,
                    [data-testid="fms-preview-modal"][data-editorial="true"] input,
                    [data-testid="fms-preview-modal"][data-editorial="true"] textarea,
                    [data-testid="fms-preview-modal"][data-editorial="true"] select,
                    [data-testid="fms-preview-modal"][data-editorial="true"] [role="tab"],
                    [data-testid="fms-preview-modal"][data-editorial="true"] [role="tablist"],
                    [data-testid="fms-preview-modal"][data-editorial="true"] [class*="rounded-sm"],
                    [data-testid="fms-preview-modal"][data-editorial="true"] [class*="rounded-md"],
                    [data-testid="fms-preview-modal"][data-editorial="true"] [class*="rounded-lg"],
                    [data-testid="fms-preview-modal"][data-editorial="true"] [class*="rounded-xl"],
                    [data-testid="fms-preview-modal"][data-editorial="true"] [class*="rounded-2xl"],
                    [data-testid="fms-preview-modal"][data-editorial="true"] [class*="rounded-3xl"] {
                        border-radius: 0 !important;
                    }
                    [data-testid="fms-preview-modal"][data-editorial="true"] .sayz-fms-step h1,
                    [data-testid="fms-preview-modal"][data-editorial="true"] .sayz-fms-step h2,
                    [data-testid="fms-preview-modal"][data-editorial="true"] .sayz-fms-step h3 {
                        font-weight: 400 !important;
                        letter-spacing: -0.01em !important;
                    }
                    [data-testid="fms-preview-modal"][data-editorial="true"] [class*="font-bold"],
                    [data-testid="fms-preview-modal"][data-editorial="true"] [class*="font-semibold"] {
                        font-weight: 500 !important;
                    }
                    [data-testid="fms-preview-modal"][data-editorial="true"] [class*="uppercase"] {
                        font-weight: 400 !important;
                        letter-spacing: 0.14em !important;
                    }
                    [data-testid="fms-preview-modal"][data-editorial="true"] [class*="tabular-nums"] {
                        font-weight: 300 !important;
                        letter-spacing: 0.02em !important;
                    }
                `}</style>
            )}

            {/* ====================================================================
                BOLD SCOPED CSS — Field Notes "lite" ekolü:
                  • Radius 0 (keskin köşe — field notes imzası KORUNDU)
                  • Modal dış çerçeve hairline (0.5px) — boğucu değil
                  • Input/select border 1px nefes alan + focus'ta 1.5px
                  • Hero başlıklar 700 (800 agresifti), uppercase 700 wide tracking KORUNDU
                  • Rakamsal değerler 600 tabular — field notes "stamp" hissi KORUNDU
                  • Primary CTA (bg + beyaz) + Secondary (outlined 1.5px) → component'lerde render
                ==================================================================== */}
            {isBold && (
                <style>{`
                    [data-testid="fms-preview-modal"][data-bold="true"] button,
                    [data-testid="fms-preview-modal"][data-bold="true"] input,
                    [data-testid="fms-preview-modal"][data-bold="true"] textarea,
                    [data-testid="fms-preview-modal"][data-bold="true"] select,
                    [data-testid="fms-preview-modal"][data-bold="true"] [role="tab"],
                    [data-testid="fms-preview-modal"][data-bold="true"] [role="tablist"],
                    [data-testid="fms-preview-modal"][data-bold="true"] [class*="rounded-sm"],
                    [data-testid="fms-preview-modal"][data-bold="true"] [class*="rounded-md"],
                    [data-testid="fms-preview-modal"][data-bold="true"] [class*="rounded-lg"],
                    [data-testid="fms-preview-modal"][data-bold="true"] [class*="rounded-xl"],
                    [data-testid="fms-preview-modal"][data-bold="true"] [class*="rounded-2xl"],
                    [data-testid="fms-preview-modal"][data-bold="true"] [class*="rounded-3xl"] {
                        border-radius: 0 !important;
                    }
                    /* Input/select/nested buton border — 1px nefes alan, varsayılan border rengi
                       (foreground değil → modalla uyumlu, boğucu değil) */
                    [data-testid="fms-preview-modal"][data-bold="true"] button[class*="border"],
                    [data-testid="fms-preview-modal"][data-bold="true"] input[class*="border"],
                    [data-testid="fms-preview-modal"][data-bold="true"] textarea[class*="border"],
                    [data-testid="fms-preview-modal"][data-bold="true"] select[class*="border"] {
                        border-width: 1px !important;
                    }
                    /* Focus/hover/selected — 1.5px koyu border (etkileşim sinyali) */
                    [data-testid="fms-preview-modal"][data-bold="true"] button[class*="border"]:hover,
                    [data-testid="fms-preview-modal"][data-bold="true"] button[class*="border-foreground"],
                    [data-testid="fms-preview-modal"][data-bold="true"] input[class*="border"]:focus,
                    [data-testid="fms-preview-modal"][data-bold="true"] input[class*="border"]:focus-visible,
                    [data-testid="fms-preview-modal"][data-bold="true"] textarea[class*="border"]:focus {
                        border-width: 1.5px !important;
                    }
                    /* Başlıklar — 700 bold (800 agresifti), hafif negatif tracking */
                    [data-testid="fms-preview-modal"][data-bold="true"] .sayz-fms-step h1,
                    [data-testid="fms-preview-modal"][data-bold="true"] .sayz-fms-step h2,
                    [data-testid="fms-preview-modal"][data-bold="true"] .sayz-fms-step h3 {
                        font-weight: 700 !important;
                        letter-spacing: -0.015em !important;
                    }
                    /* font-bold class'ları — 700 (800 → 700 nefes al) */
                    [data-testid="fms-preview-modal"][data-bold="true"] [class*="font-bold"] {
                        font-weight: 700 !important;
                    }
                    [data-testid="fms-preview-modal"][data-bold="true"] [class*="font-semibold"] {
                        font-weight: 650 !important;
                    }
                    /* Uppercase label'lar — 700 + wide tracking KORUNDU (field notes DNA) */
                    [data-testid="fms-preview-modal"][data-bold="true"] [class*="uppercase"] {
                        font-weight: 700 !important;
                        letter-spacing: 0.04em !important;
                    }
                    /* Rakamsal değerler — 600 tabular (field notes stamp) KORUNDU */
                    [data-testid="fms-preview-modal"][data-bold="true"] [class*="tabular-nums"] {
                        font-weight: 600 !important;
                    }
                    /* BOY/KİLO/YAŞ içindeki number input — "border-0" class'ı 
                       nedeniyle input[class*="border"] kuralı yanlışlıkla 1px border
                       veriyordu. Bold'da bu input'un gerçekten border'sız kalması için override. */
                    [data-testid="fms-preview-modal"][data-bold="true"] [data-slot="stepper-field"] input {
                        border-width: 0 !important;
                    }
                `}</style>
            )}

            {/* ─── Mobile tightening (≤480px) ─────────────────────────────
                Masaüstünün ferahlığı (px-6 py-5 → 24/20 px) mobile'da
                parmak ergonomisine daraltılır. Scoped: yalnızca wizard
                modal + mobile viewport. Font skalaları + button boyları
                aynı dilde kalır (karikatürleşme yok). */}
            <style>{`
                @media (max-width: 480px) {
                    [data-testid="fms-preview-modal"] .sayz-fms-header,
                    [data-testid="fms-preview-modal"] .sayz-fms-progress,
                    [data-testid="fms-preview-modal"] .sayz-fms-step,
                    [data-testid="fms-preview-modal"] .sayz-fms-footer {
                        padding-left: 16px !important;
                        padding-right: 16px !important;
                    }
                    [data-testid="fms-preview-modal"] .sayz-fms-header {
                        padding-top: 14px !important;
                        padding-bottom: 10px !important;
                    }
                    [data-testid="fms-preview-modal"] .sayz-fms-progress {
                        padding-bottom: 12px !important;
                    }
                    [data-testid="fms-preview-modal"] .sayz-fms-step {
                        padding-top: 18px !important;
                        padding-bottom: 18px !important;
                    }
                    [data-testid="fms-preview-modal"] .sayz-fms-footer {
                        padding-top: 12px !important;
                        padding-bottom: 12px !important;
                    }
                    /* Başlıklar mobile'da hafifçe küçülür — hero size
                       (72px) merkez eleman olduğu için ona dokunulmaz,
                       sadece h3 soru başlıkları ve step label'ları. */
                    [data-testid="fms-preview-modal"] .sayz-fms-step h3 {
                        font-size: 19px !important;
                        line-height: 1.3 !important;
                    }
                    /* Body option kartları: px-5 py-4 → px-4 py-3 (parmak
                       hedefi 44px üstü kalır çünkü üç option sırası aynı
                       tap-column'da; sıkma ritmi daha editorial). */
                    [data-testid="fms-preview-modal"] [data-testid^="fms-body-opt-"],
                    [data-testid="fms-preview-modal"] [data-testid^="fms-fit-"] {
                        padding: 12px 14px !important;
                    }

                    /* ─── Mobile overflow handling ────────────────────────
                       Modal doğal yüksekliği viewport'u aşıyorsa (uzun
                       body/fit step'lerinde yaygın), içerik envelope'u
                       dikey kayar. Scrollbar GİZLİ (editorial look):
                       scrollbar-width none (FF) + webkit ::scrollbar
                       display none. Beden tablosu modalındaki görünür
                       scroll barından farklı olarak burada sadece touch
                       gesture ile kaydırma hissi — temiz, Instagram
                       sheet / Stripe checkout pattern'i. */
                    [data-testid="fms-preview-modal"] .sayz-preview-scroll {
                        overflow-y: auto !important;
                        max-height: calc(100dvh - 24px);
                        -webkit-overflow-scrolling: touch;
                        overscroll-behavior: contain;
                        scrollbar-width: none;
                    }
                    [data-testid="fms-preview-modal"] .sayz-preview-scroll::-webkit-scrollbar {
                        width: 0;
                        height: 0;
                        display: none;
                    }
                }
            `}</style>
            {/* Header — title + close on one row, progress bar as its own row
                so it spans the same padding gutter as the wizard content and
                aligns cleanly on BOTH sides (not choked by the X button). */}
            <div className="border-b border-border/60 shrink-0">
                <div className="sayz-fms-header flex items-center justify-between gap-3 px-6 pt-5 pb-3">
                    <div className="min-w-0 flex-1">
                        <div className="text-[15px] font-semibold truncate tracking-tight text-foreground" data-testid="fms-preview-title">
                            {modalTitle}
                        </div>
                    </div>
                    <button
                        type="button"
                        aria-label="Kapat"
                        className="shrink-0 h-7 w-7 grid place-items-center rounded-md text-muted-foreground/50 hover:text-foreground hover:bg-muted/50 transition-colors"
                        onClick={() => {
                            // Prefer the explicit onClose callback passed via
                            // the WizardPreviewProvider (used by the Shadow DOM
                            // widget to close its modal portal). Fall back to
                            // postMessage for legacy iframe embeds, and no-op
                            // in the admin panel preview.
                            if (typeof onClose === "function") {
                                onClose();
                                return;
                            }
                            try {
                                if (typeof window !== "undefined" && window.parent && window.parent !== window) {
                                    window.parent.postMessage({ sayz: true, type: "close" }, "*");
                                }
                            } catch (_) { /* noop */ }
                        }}
                        data-testid="fms-close-btn"
                    >
                        <X className="h-[18px] w-[18px]" strokeWidth={1.5} />
                    </button>
                </div>
                <div className="sayz-fms-progress px-6 pb-4">
                    <ProgressBar step={step} total={visibleSteps.length} accent={accent} />
                </div>
            </div>

            {/* Dynamic-height content envelope. Her ekranın doğal yüksekliği
                ölçülür ve 350 ms cubic-bezier curve ile animasyonlu olarak
                uygulanır — Stripe / Linear / Shopify Checkout pattern'i.
                Zıplama yok, boşluk yok, scroll yok. Minimum 320 px floor
                beklenmedik boş state'leri yakalar; `max-h-[85vh]` çok uzun
                içerik durumlarını (teorik) viewport'a sığdırır. İçerik
                padding'i dış conteyner'da değil, iç `sayz-fms-step`'te —
                böylece measurement'ta padding dahil edilir, animasyon
                "modal kendisi büyüyor" hissini verir. */}
            <div
                className="sayz-preview-scroll overflow-hidden"
                style={{
                    height: measuredHeight ? `${measuredHeight}px` : "auto",
                    minHeight: "280px",
                    transition: "height 350ms cubic-bezier(0.32, 0.72, 0, 1)",
                }}
            >
                <div ref={contentRef} key={stepKey} className="sayz-fms-step px-6 py-6">
                    {currentId === "welcome" && (
                        <WelcomeStep
                            profile={profile}
                            copy={stepCopy}
                            resultCopy={resultCopy}
                            accent={accent}
                            mode={welcomeMode}
                            analysis={welcomeAnalysis}
                            recommendation={welcomeRecommendation}
                            outOfRangeStrategy={settings.outOfRangeStrategy}
                            hostStockStatus={hostStockStatus}
                            highlightedKey={lastAnsweredField}
                            onMissingBodySelect={(kind, value) => {
                                setField(kind, value);
                                // Bu cevaptan sonra hâlâ eksik alan var mı? Yoksa result'a atla.
                                const remainingAfter = welcomeAnalysis.missing.filter(
                                    (f) => f !== kind,
                                );
                                if (remainingAfter.length === 0) {
                                    setTimeout(() => goToResult(), 380);
                                }
                            }}
                        />
                    )}
                    {currentId === "basic" && (
                        <BasicInfoStep profile={profile} setField={setField} copy={stepCopy} accent={accent} requiredBodyFields={requiredBodyFields} />
                    )}
                    {currentId === "fit" && (
                        <FitPrefStep profile={profile} onSelect={(v) => autoAdvance("fit", v)} copy={stepCopy} accent={accent} />
                    )}
                    {currentId?.startsWith("body:") && (
                        <BodyStep
                            kind={currentId.split(":")[1]}
                            profile={profile}
                            onSelect={(v) => autoAdvance(currentId.split(":")[1], v)}
                            copy={stepCopy}
                            accent={accent}
                        />
                    )}
                    {currentId === "result" && !recommendation && (
                        <MeasuringTapeLoader
                            profile={profile}
                            accent={accent}
                            copy={resultCopy}
                        />
                    )}
                    {currentId === "result" && recommendation && (
                        <ResultStep
                            recommendation={recommendation}
                            profile={profile}
                            copy={resultCopy}
                            outOfRangeStrategy={settings.outOfRangeStrategy}
                            previewRefusal={previewRefusal}
                            accent={accent}
                            hostStockStatus={hostStockStatus}
                        />
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="sayz-fms-footer px-6 py-4 border-t border-border/60 shrink-0 flex items-center justify-between gap-2 bg-surface">
                <div className="flex items-center gap-1">
                    {showBackButton && (
                        <button
                            type="button"
                            onClick={goBack}
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border/70 text-[12px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted/40 hover:border-foreground/30 transition-all"
                            data-testid="fms-preview-back"
                        >
                            <ArrowLeft className="h-3 w-3" strokeWidth={1.5} />
                            Geri
                        </button>
                    )}
                    {showEditLink && (
                        <button
                            type="button"
                            onClick={goToBasic}
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border/70 text-[12px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted/40 hover:border-foreground/30 transition-all"
                            data-testid="fms-preview-edit-link"
                        >
                            <Pencil className="h-3 w-3" strokeWidth={1.5} />
                            Bilgilerimi Değiştir
                        </button>
                    )}
                </div>
                <FooterAction
                    currentId={currentId}
                    canProceed={canProceed}
                    recommendation={recommendation}
                    welcomeRecommendation={welcomeRecommendation}
                    hostStockStatus={hostStockStatus}
                    previewRefusal={previewRefusal}
                    outOfRangeStrategy={settings.outOfRangeStrategy}
                    welcomeMode={welcomeMode}
                    welcomeAnalysis={welcomeAnalysis}
                    stepCopy={stepCopy}
                    resultCopy={resultCopy}
                    accent={accent}
                    widgetTemplate={widgetTemplate}
                    widgetTokens={widgetTokens}
                    sizeTableButtonText={sizeTableButtonText}
                    onNext={goNext}
                    onWelcomeConfirm={goToResult}
                    onPartialContinue={onPartialContinue}
                    onSelectSize={handleSelectSize}
                    onOpenSizeChart={onOpenSizeChart}
                />
            </div>
        </div>
    );
};
